import { set } from '@vue/composition-api';
import { pinia } from '@/plugins/pinia';
import { CoreUtils, createConditionChecker, getExpressionParser, getVariableParser } from '@/lib/nuclicore-core';
import { useVariableStore } from '@/store/variableStore';
import { isPlatformArray } from '@/lib/nuclicore-core/helpers/utils';

const variableStore = useVariableStore(pinia);

const expressionParser = getExpressionParser();
const conditionChecker = createConditionChecker();
const variableParser = getVariableParser();

export default new class FormRenderer {
    async check(data, values) {
        const fieldsToProcess = [];
        for (let section of data.sections) {
            set(section, 'show', !!(await conditionChecker.isSatisfied(section.condition)));
            for (const column of section.fields) {
                for (const field of column) {
                    if (field.type === 'grid') {
                        const cellElements = field?.properties?.basic?.cells?.data?.flatMap(row => row.filter(
                            cell => cell?.properties?.basic?.element?.type
                        ));
                        fieldsToProcess.push(
                            ...cellElements.map(
                                cell => this.renderForm(cell.properties.basic.element, values, section.show)
                            )
                        );
                    }
                    fieldsToProcess.push(this.renderForm(field, values, section.show));
                }
            }
        }
        await Promise.all(fieldsToProcess);
        return data;
    }

    /**
     * Parses the raw values of a field and update its values with parsed value
     * @param {object} field 
     * @param {object} values Contains parsed values of all fields
     * @param {boolean} isSectionVisible
     */
    async renderForm(field, values, isSectionVisible = true) {
        set(field, 'show', !!(await conditionChecker.isSatisfied(field.condition)));
        const fieldBasicProperties = field?.properties?.basic;
        const validationProperties = field?.properties?.validation;
        const validationExpressions = validationProperties?.expressions;
        const fieldValidationProperties = field?.properties?.validation;

        if (field.id) {
            const checkFieldValueIgnored = fieldBasicProperties?.isIgnoreHiddenFieldValueDynamic ? (await expressionParser.parseAndDecode(fieldBasicProperties?.ignoreHiddenFieldValue, 'strip')).value : fieldBasicProperties?.ignoreHiddenFieldValue;
            const isFieldValueIgnored = checkFieldValueIgnored ? !(isSectionVisible && field.show) : false;
            const existingIgnoredValue = variableStore.isVariableIgnored(field.id);
            if (existingIgnoredValue !== isFieldValueIgnored) {
                variableStore.updateVariableById(
                    {
                        type: 'field',
                        value: field.id
                    }, 
                    {
                        isValueIgnored: isFieldValueIgnored
                    }
                );
            }
        }

        if (!fieldBasicProperties) {
            return;
        }

        if (field.type === 'text' || field.type === 'toc') {
            const parsedLinkText = await this.parseDynamicLinks(fieldBasicProperties.rawText, values);
            fieldBasicProperties.text = (await expressionParser.parseAndDecode(parsedLinkText, 'skip', {
                variableParserOptions: {
                    joinArrays: true
                }
            })).value;
            const images = fieldBasicProperties.text.match(/(\bdata:image\/png;base64\S+\S+)/ig);
            if (images?.length) {
                images.forEach(image => {
                    fieldBasicProperties.text = fieldBasicProperties.text.replace(image, `<img src="${image}" class="var-img" width="340" height="150"  alt=""/>`);
                });
            }

        } else if (field.type === 'data-table') {

            let data = [];

            fieldBasicProperties.headers = [];
            fieldBasicProperties.data = [];

            for (const row of fieldBasicProperties.columns) {
                const rowExpressions = row?.expressions;

                const label = (await expressionParser.parseAndDecode(rowExpressions?.label, 'strip')).value || row.label;
                fieldBasicProperties.headers.push({
                    field: label,
                    label,
                    visible: row.visible
                });

                data.push({
                    label,
                    value: (await expressionParser.parseAndDecode(row.value, 'strip')).value
                });
            }

            // handle escaping double quotes from the single value columns and convert to platform arrays (already done by default for multi-values columns) 
            data = data.map(column => ({
                ...column,
                value: isPlatformArray(column.value || '') ? column.value : `{${JSON.stringify(column.value || '')}}`
            }));

            if(fieldBasicProperties.defaultFilteringColumns?.length){
                fieldBasicProperties.defaultFilteringColumns = await Promise.all(
                    fieldBasicProperties?.defaultFilteringColumns?.map(async (column) => {
                        if(column.keywords){
                            column.keywords = (await expressionParser.parseAndDecode(column.keywords, 'strip'))?.value?.replace(/^\{|}$/g, '');
                        }
                        return column;
                    })
                );
            }
              
            let orderedData = [];
            data.forEach((row, index) => {
                let singleRow = {};
                row.value = row.value.replace(/<[^>]*>?/gm, '').trim();

                const decodedValue = variableParser.decodeValue(row.value);
                const rowValues = decodedValue ? CoreUtils.parsePlatformArray(decodedValue) : [];
                rowValues.forEach((value, i) => {
                    if (typeof singleRow[i] === 'undefined') {
                        singleRow[i] = {};
                    }
                    singleRow[i][row.label] = value;
                });
                orderedData.push(singleRow);
            });

            let groupedData = [];

            for (let i = 0; i < orderedData.length; i++) {
                for (const order in orderedData[i]) {
                    if (typeof groupedData[order] === 'undefined') {
                        groupedData[order] = [];
                    }
                    groupedData[order].push(orderedData[i][order]);
                }
            }

            groupedData.forEach(d => {
                fieldBasicProperties.data.push(d.reduce(((r, c) => Object.assign(r, c)), {}));
            });

            const fieldExpressions = fieldValidationProperties?.expressions;
            if(fieldBasicProperties?.isDefaultSortingDynamic){
                fieldBasicProperties.hasDefaultSorting = !!(await expressionParser.parseAndDecode(fieldExpressions.hasDefaultSorting, 'strip')).value;
            }
            if(fieldBasicProperties?.isDefaultFilteringDynamic){
                fieldBasicProperties.hasDefaultFiltering = !!(await expressionParser.parseAndDecode(fieldExpressions.hasDefaultFiltering, 'strip')).value;
            }

        } else if (field.type === 'e-signature') {
            field.value = values[field.id];
        } else if (field.type === 'payment-gateway') {
            const { payload, serviceProvider } = fieldBasicProperties;
            if (serviceProvider === 'Element') {
                if (payload && Object.keys(payload).length) {
                    fieldBasicProperties.payload = {
                        ...payload,
                        quoteId: payload.quoteId ? (await expressionParser.parseAndDecode(payload.quoteId, 'strip')).value : '',
                        signedUrl: payload.signedUrl ? (await expressionParser.parseAndDecode(payload.signedUrl, 'strip')).value : '',
                        environment: payload.environment ? (await expressionParser.parseAndDecode(payload.environment, 'strip')).value : ''
                    };
                }
            }
        } else if (field.type === 'tariff-comparison') {
            const fieldExpressions = fieldBasicProperties?.expressions;
            if (fieldExpressions?.highlightedTariffText) {
                fieldBasicProperties.highlightedTariffText = (await expressionParser.parseAndDecode(fieldExpressions.highlightedTariffText, 'strip')).value;
            }
            if (fieldBasicProperties?.tariffs) {
                await Promise.all(fieldBasicProperties.tariffs.map(async tariff => {
                    const tariffExpressions = tariff.expressions;
                    if (tariff.isVisible === undefined) {
                        tariff.isVisible = true;
                    } else if (tariff?.isVisibilityDynamic && tariffExpressions?.isVisible) {
                        tariff.isVisible = (await expressionParser.parseAndDecode(tariffExpressions.isVisible, 'strip')).value;
                    }
                    if (tariffExpressions?.isHighlighted) {
                        tariff.isHighlighted = (await expressionParser.parseAndDecode(tariffExpressions.isHighlighted, 'strip')).value;
                    }
                    if (tariffExpressions?.title && tariff?.title) {
                        tariff.title = (await expressionParser.parseAndDecode(tariffExpressions.title, 'strip')).value;
                    }
                    if (tariffExpressions?.price && tariff?.price) {
                        tariff.price = (await expressionParser.parseAndDecode(tariffExpressions.price, 'strip')).value;
                    }
                    if (tariffExpressions?.description && tariff?.description) {
                        const parsedLinkText = await this.parseDynamicLinks(tariffExpressions.description, values);
                        tariff.description = (await expressionParser.parseAndDecode(parsedLinkText, 'skip')).value;
                    }
                    if (tariff.isButtonVisible === undefined) {
                        tariff.isButtonVisible = true;
                    } else if (tariff?.isButtonVisibilityDynamic && tariffExpressions?.isButtonVisible) {
                        tariff.isButtonVisible = (await expressionParser.parseAndDecode(tariffExpressions.isButtonVisible, 'strip')).value;
                    }
                }));
            }
        } else if (field.type === 'date') {
            const fieldExpressions = fieldBasicProperties?.expressions;
            if (fieldBasicProperties?.isTimePickerToggleDynamic) {
                fieldBasicProperties.hasTimePicker = !!(await expressionParser.parseAndDecode(fieldExpressions.hasTimePicker, 'strip')).value;
            }
            if(fieldBasicProperties?.isPlaceholderDynamic){
                fieldBasicProperties.placeholder = (await expressionParser.parseAndDecode(fieldExpressions.placeholder, 'strip')).value;
            }
        } else if (field.type === 'time'){
            const fieldExpressions = fieldBasicProperties?.expressions;
            if(fieldBasicProperties?.is24HourFormatDynamic){
                fieldBasicProperties.is24HourFormat = (await expressionParser.parseAndDecode(fieldExpressions.is24HourFormat, 'strip')).value;
            }
        } else if (field.type === 'iban') {
            const fieldExpressions = fieldBasicProperties?.expressions;
            if (fieldBasicProperties?.isIbanApiValidationToggleDynamic) {
                fieldBasicProperties.hasIbanApiValidation = !!(await expressionParser.parseAndDecode(fieldExpressions.hasIbanApiValidation, 'strip')).value;
            }
        } else if(field.type === 'password') {
            const fieldExpressions = fieldBasicProperties?.expressions;
            if (fieldBasicProperties?.isPasswordVisibleDynamic) {
                fieldBasicProperties.isPasswordVisible = !!(await expressionParser.parseAndDecode(fieldExpressions.isPasswordVisible, 'strip')).value;
            }
        } else if (field.type === 'address'){
            const fieldExpressions = fieldBasicProperties?.expressions;
            if (fieldBasicProperties?.hasStateDynamic) {
                fieldBasicProperties.hasState = !!(await expressionParser.parseAndDecode(fieldExpressions.hasState, 'strip')).value;
            }
            if (fieldBasicProperties?.hasDistrictDynamic) {
                fieldBasicProperties.hasDistrict = !!(await expressionParser.parseAndDecode(fieldExpressions.hasDistrict, 'strip')).value;
            }                     
            if (fieldBasicProperties?.hasCountryDynamic) {
                fieldBasicProperties.hasCountry = !!(await expressionParser.parseAndDecode(fieldExpressions.hasCountry, 'strip')).value;
            }
            if (fieldBasicProperties?.subFieldsProperties?.zipCode && fieldExpressions?.subFields?.zipCode?.maxLength) {
                field.properties.basic.subFieldsProperties.zipCode.maxLength = (await  expressionParser.parseAndDecode(fieldExpressions.subFields.zipCode.maxLength, 'strip')).value;
            }
        } else if (field.type === 'file-upload'){
            const fieldExpressions = fieldValidationProperties?.expressions;
            if(fieldValidationProperties?.isCustomFormatDynamic){
                fieldValidationProperties.customFormat = (await expressionParser.parseAndDecode(fieldExpressions.customFormat, 'strip')).value;
            }
            if(fieldValidationProperties?.sizeRestriction){
                fieldValidationProperties.sizeRestriction = (await expressionParser.parseAndDecode(fieldExpressions.sizeRestriction, 'strip')).value;
            }
        } else if (field.type === 'iframe') {
            fieldBasicProperties.url = (await expressionParser.parseAndDecode(fieldBasicProperties.url, 'strip')).value;
        }

        if(fieldBasicProperties.isDisabled !== undefined && fieldBasicProperties.isDisabledDynamic){
            fieldBasicProperties.isDisabled = !!(await expressionParser.parseAndDecode(validationExpressions?.isDisabled, 'strip')).value;
        }

        if (validationProperties) {
            if(validationProperties.isRequired !== undefined && validationProperties.isRequiredDynamic){
                validationProperties.isRequired = !!(await expressionParser.parseAndDecode(validationExpressions.isRequired, 'strip')).value;
            }
            if(validationProperties.isRequired !== undefined && validationProperties.isRequiredMessageDynamic){
                validationProperties.isRequiredMessage = (await expressionParser.parseAndDecode(validationExpressions.isRequiredMessage, 'strip')).value;
            }
            if(validationProperties.hasRegex !== undefined && validationProperties.isRegexMessageDynamic){
                validationProperties.hasRegexMessage = (await expressionParser.parseAndDecode(validationExpressions.hasRegexMessage, 'strip')).value;
            }
            if(validationProperties.message !== undefined && validationProperties.isMessageDynamic){
                validationProperties.message = (await expressionParser.parseAndDecode(validationExpressions.message, 'strip')).value;
            }
            if(validationProperties.rangeErrorMessage !== undefined && validationProperties.isRangeErrorMessageDynamic){
                validationProperties.rangeErrorMessage = (await expressionParser.parseAndDecode(validationExpressions.rangeErrorMessage, 'strip')).value;
            }
        }
        
    }

    async parseDynamicLinks (rawValue, values) {
        const linkTag = {
            selector: 'a',
            attribute: 'href'
        };
        const parsedText = await expressionParser.parseElementAttribute(linkTag, rawValue, values);
        return parsedText;
    }
};
